import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import {
  imageStudioService,
  StudioAsset,
  StudioPreset,
  StudioGallery,
  StudioToolSpec,
} from "../service/apiService";
import { useUser } from "./UserContext";
import { prepareImage, ImagePrepError } from "../utils/prepareImage";

export type StudioMode = "generate" | "edit" | "control" | "style" | "upscale";

export interface StudioResult {
  assetId: string;
  url: string;
  s3Key: string;
  tool: string;
  seed?: number | null;
  outputFormat: string;
}

interface ImageStudioContextType {
  // Preset state
  presets: StudioPreset[];
  presetsLoading: boolean;
  presetMode: boolean; // Presets section is active (vs raw tools)
  activePreset: StudioPreset | null;
  presetNeedsMask: boolean;
  openPresets: () => void;
  exitPresets: (mode: StudioMode) => void;
  selectPreset: (key: string) => void;
  closePreset: () => void;

  // Galleries
  galleries: StudioGallery[];
  activeGalleryId: string | null;
  activeGallery: StudioGallery | null;
  galleriesLoading: boolean;
  createGallery: (name?: string) => Promise<void>;
  renameGallery: (galleryId: string, name: string) => Promise<void>;
  deleteGallery: (galleryId: string) => Promise<void>;
  switchGallery: (galleryId: string) => void;

  // Tools / params
  tools: StudioToolSpec[];
  modes: StudioMode[];
  mode: StudioMode;
  setMode: (mode: StudioMode) => void;
  toolKey: string;
  selectTool: (key: string) => void;
  currentSpec: StudioToolSpec | null;
  toolsForMode: StudioToolSpec[];
  params: Record<string, any>;
  setParam: (name: string, value: any) => void;
  randomizeSeed: () => void;
  catalogLoading: boolean;

  // Reference
  referenceKey: string | null;
  referencePreviewUrl: string | null;
  uploadingReference: boolean;
  uploadReference: (file: File) => Promise<void>;
  clearReference: () => void;

  // Mask (brush tools)
  maskB64: string | null;
  setMaskB64: (mask: string | null) => void;

  // Style image (style-transfer)
  styleReferenceKey: string | null;
  styleReferencePreviewUrl: string | null;
  uploadingStyleReference: boolean;
  uploadStyleReference: (file: File) => Promise<void>;
  clearStyleReference: () => void;

  // Generation
  result: StudioResult | null;
  results: StudioResult[]; // the full variant batch of the last generation
  selectResult: (assetId: string) => void;
  variants: number;
  setVariants: (n: number) => void;
  generating: boolean;
  generate: () => Promise<void>;
  clearResult: () => void;
  loadAsInput: (asset: { s3_key: string; presigned_url: string; asset_id: string }) => void;
  // Load an asset's stored generation recipe (tool/preset + params + seed +
  // source image when still present) back into the controls.
  applySettings: (asset: StudioAsset) => void;

  // Assets
  assets: StudioAsset[];
  loadingAssets: boolean;
  refreshAssets: () => Promise<void>;
  deleteItem: (assetId: string) => Promise<void>;
}

const ImageStudioContext = createContext<ImageStudioContextType | undefined>(undefined);

const ACTIVE_GALLERY_KEY = "kyva-studio-active-gallery";
const MODE_ORDER: StudioMode[] = ["generate", "edit", "control", "style", "upscale"];

// Does a preset field's show_when / needs_mask_when condition hold for the
// current answers?
export function matchCondition(
  cond: { field: string; equals: string } | null | undefined,
  params: Record<string, any>
): boolean {
  if (!cond) return false;
  return (params[cond.field] ?? "") === cond.equals;
}

function defaultsFor(spec: StudioToolSpec): Record<string, any> {
  const params: Record<string, any> = {};
  spec.params.forEach((p) => {
    if (p.default !== undefined && p.default !== null) params[p.name] = p.default;
  });
  return params;
}

function presetDefaults(preset: StudioPreset): Record<string, any> {
  const params: Record<string, any> = {};
  preset.fields.forEach((f) => {
    if (f.default !== undefined && f.default !== null) params[f.name] = f.default;
  });
  return params;
}

export const ImageStudioProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userId } = useUser();

  // Galleries
  const [galleries, setGalleries] = useState<StudioGallery[]>([]);
  const [activeGalleryId, setActiveGalleryId] = useState<string | null>(null);
  const [galleriesLoading, setGalleriesLoading] = useState(false);

  // Presets
  const [presets, setPresets] = useState<StudioPreset[]>([]);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [presetMode, setPresetMode] = useState(true); // open on the preset gallery
  const [activePresetKey, setActivePresetKey] = useState<string | null>(null);

  // Tools / params
  const [tools, setTools] = useState<StudioToolSpec[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [toolKey, setToolKey] = useState<string>("");
  const [params, setParams] = useState<Record<string, any>>({});

  // Reference / result / assets
  const [referenceKey, setReferenceKey] = useState<string | null>(null);
  const [referencePreviewUrl, setReferencePreviewUrl] = useState<string | null>(null);
  const [uploadingReference, setUploadingReference] = useState(false);
  const [parentAssetId, setParentAssetId] = useState<string | null>(null);
  const [maskB64, setMaskB64] = useState<string | null>(null);
  const [styleReferenceKey, setStyleReferenceKey] = useState<string | null>(null);
  const [styleReferencePreviewUrl, setStyleReferencePreviewUrl] = useState<string | null>(null);
  const [uploadingStyleReference, setUploadingStyleReference] = useState(false);
  const [result, setResult] = useState<StudioResult | null>(null);
  const [results, setResults] = useState<StudioResult[]>([]);
  const [variants, setVariants] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [assets, setAssets] = useState<StudioAsset[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);

  // Live mirror of the active gallery so async work (a generation in flight)
  // can detect that the user switched galleries before it finished.
  const activeGalleryIdRef = useRef<string | null>(null);
  useEffect(() => {
    activeGalleryIdRef.current = activeGalleryId;
  }, [activeGalleryId]);

  const currentSpec = useMemo(
    () => tools.find((t) => t.key === toolKey) || null,
    [tools, toolKey]
  );
  const mode: StudioMode = (currentSpec?.mode as StudioMode) || "generate";
  const modes = useMemo(
    () => MODE_ORDER.filter((m) => tools.some((t) => t.mode === m)),
    [tools]
  );
  const toolsForMode = useMemo(() => tools.filter((t) => t.mode === mode), [tools, mode]);
  const activeGallery = useMemo(
    () => galleries.find((p) => p.gallery_id === activeGalleryId) || null,
    [galleries, activeGalleryId]
  );
  const activePreset = useMemo(
    () => presets.find((p) => p.key === activePresetKey) || null,
    [presets, activePresetKey]
  );
  const presetNeedsMask = useMemo(
    () => (presetMode && activePreset ? matchCondition(activePreset.needs_mask_when, params) : false),
    [presetMode, activePreset, params]
  );

  // Load the preset catalog once the user is known.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        setPresetsLoading(true);
        const list = await imageStudioService.getPresets();
        if (!cancelled) setPresets(list);
      } catch (err) {
        console.error("Failed to load presets:", err);
      } finally {
        if (!cancelled) setPresetsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Load the tool catalog once the user is known.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        setCatalogLoading(true);
        const catalog = await imageStudioService.getTools();
        if (cancelled) return;
        setTools(catalog);
        const first = catalog[0];
        if (first) {
          setToolKey(first.key);
          setParams(defaultsFor(first));
        }
      } catch (err) {
        console.error("Failed to load image studio tools:", err);
        toast.error("Failed to load Image Studio tools");
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Load galleries; pick the last-used one (or the first) as active.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        setGalleriesLoading(true);
        const list = await imageStudioService.listGalleries();
        if (cancelled) return;
        setGalleries(list);
        const remembered = window.localStorage.getItem(ACTIVE_GALLERY_KEY);
        const pick =
          (remembered && list.find((p) => p.gallery_id === remembered)?.gallery_id) ||
          list[0]?.gallery_id ||
          null;
        setActiveGalleryId(pick);
      } catch (err) {
        console.error("Failed to load galleries:", err);
        toast.error("Failed to load galleries");
      } finally {
        if (!cancelled) setGalleriesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const refreshAssets = useCallback(async () => {
    if (!activeGalleryId) {
      setAssets([]);
      return;
    }
    try {
      setLoadingAssets(true);
      const items = await imageStudioService.getAssets(activeGalleryId);
      setAssets(items);
      // Keep the gallery's asset_count badge in sync with what's actually in
      // the gallery (uploads + generated). The backend counter can drift; a
      // fresh listing is the source of truth.
      setGalleries((prev) =>
        prev.map((g) =>
          g.gallery_id === activeGalleryId ? { ...g, asset_count: items.length } : g
        )
      );
    } catch (err) {
      console.error("Failed to load assets:", err);
    } finally {
      setLoadingAssets(false);
    }
  }, [activeGalleryId]);

  const clearReference = useCallback(() => {
    setReferenceKey(null);
    setReferencePreviewUrl((prev) => {
      if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
      return null;
    });
    setParentAssetId(null);
    setMaskB64(null);
  }, []);

  const clearStyleReference = useCallback(() => {
    setStyleReferenceKey(null);
    setStyleReferencePreviewUrl((prev) => {
      if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const uploadStyleReference = useCallback(
    async (file: File) => {
      if (!activeGalleryId) {
        toast.error("Select or create a gallery first");
        return;
      }
      let prepared: File;
      try {
        prepared = await prepareImage(file);
      } catch (e) {
        toast.error(e instanceof ImagePrepError ? e.message : "Could not read that image");
        return;
      }
      try {
        setUploadingStyleReference(true);
        const { s3Key } = await imageStudioService.uploadReference(activeGalleryId, prepared);
        setStyleReferenceKey(s3Key);
        setStyleReferencePreviewUrl((prev) => {
          if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
          return URL.createObjectURL(prepared);
        });
        refreshAssets();
      } catch (err) {
        console.error("Style image upload failed:", err);
        toast.error("Failed to upload style image");
      } finally {
        setUploadingStyleReference(false);
      }
    },
    [activeGalleryId, refreshAssets]
  );

  // Reload assets whenever the active gallery changes; reset working state.
  // The input image, result and asset list all belong to the previous gallery,
  // so they are dropped immediately — nothing stale may linger on screen.
  useEffect(() => {
    setAssets([]);
    if (activeGalleryId) {
      window.localStorage.setItem(ACTIVE_GALLERY_KEY, activeGalleryId);
      refreshAssets();
    }
    setResult(null);
    setResults([]);
    clearReference();
    clearStyleReference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGalleryId]);

  // ── Gallery actions ──
  const createGallery = useCallback(async (name?: string) => {
    try {
      const gallery = await imageStudioService.createGallery(name);
      setGalleries((prev) => [gallery, ...prev]);
      setActiveGalleryId(gallery.gallery_id);
    } catch (err) {
      console.error("Failed to create gallery:", err);
      toast.error("Failed to create gallery");
    }
  }, []);

  const renameGallery = useCallback(async (galleryId: string, name: string) => {
    try {
      await imageStudioService.renameGallery(galleryId, name);
      setGalleries((prev) =>
        prev.map((p) => (p.gallery_id === galleryId ? { ...p, name } : p))
      );
    } catch (err) {
      console.error("Failed to rename gallery:", err);
      toast.error("Failed to rename gallery");
    }
  }, []);

  const deleteGallery = useCallback(
    async (galleryId: string) => {
      try {
        await imageStudioService.deleteGallery(galleryId);
        const remaining = galleries.filter((p) => p.gallery_id !== galleryId);
        setGalleries(remaining);
        if (activeGalleryId === galleryId) {
          if (remaining[0]) {
            setActiveGalleryId(remaining[0].gallery_id);
          } else {
            await createGallery("Untitled");
          }
        }
      } catch (err) {
        console.error("Failed to delete gallery:", err);
        toast.error("Failed to delete gallery");
      }
    },
    [galleries, activeGalleryId, createGallery]
  );

  const switchGallery = useCallback((galleryId: string) => {
    setActiveGalleryId(galleryId);
  }, []);

  // ── Tool actions ──
  const selectTool = useCallback(
    (key: string) => {
      const spec = tools.find((t) => t.key === key);
      if (!spec) return;
      setToolKey(key);
      setParams((prev) => {
        const next = defaultsFor(spec);
        // Carry the user's typed prompts over to the new tool (when it has
        // them) — trying the same prompt on another model shouldn't mean
        // retyping it.
        for (const name of ["prompt", "negative_prompt"]) {
          if (prev[name] && spec.params.some((p) => p.name === name)) {
            next[name] = prev[name];
          }
        }
        return next;
      });
      setMaskB64(null);
      // The input image is studio-wide state and survives tool switches; the
      // style image is only meaningful to style tools, so drop it elsewhere.
      if (!spec.needs_style_image) clearStyleReference();
    },
    [tools, clearStyleReference]
  );

  const setMode = useCallback(
    (next: StudioMode) => {
      const firstOfMode = tools.find((t) => t.mode === next);
      if (firstOfMode) selectTool(firstOfMode.key);
    },
    [tools, selectTool]
  );

  // ── Preset actions ──
  // Switching between Presets and the raw tool tabs deliberately keeps the
  // loaded input image and the latest result: a generated image must remain
  // reusable everywhere (another preset, an edit tool, upscale, …).
  const openPresets = useCallback(() => {
    setPresetMode(true);
  }, []);

  const exitPresets = useCallback(
    (mode: StudioMode) => {
      setPresetMode(false);
      setActivePresetKey(null);
      setMode(mode);
    },
    [setMode]
  );

  const selectPreset = useCallback(
    (key: string) => {
      const preset = presets.find((p) => p.key === key);
      if (!preset) return;
      setActivePresetKey(key);
      setPresetMode(true);
      setParams(presetDefaults(preset));
    },
    [presets]
  );

  const closePreset = useCallback(() => {
    setActivePresetKey(null);
  }, []);

  const setParam = useCallback((name: string, value: any) => {
    setParams((prev) => ({ ...prev, [name]: value }));
  }, []);

  const randomizeSeed = useCallback(() => {
    setParam("seed", Math.floor(Math.random() * 4294967293) + 1);
  }, [setParam]);

  // ── Reference upload ──
  const uploadReference = useCallback(
    async (file: File) => {
      if (!activeGalleryId) {
        toast.error("Select or create a gallery first");
        return;
      }
      let prepared: File;
      try {
        prepared = await prepareImage(file);
      } catch (e) {
        toast.error(e instanceof ImagePrepError ? e.message : "Could not read that image");
        return;
      }
      try {
        setUploadingReference(true);
        const { s3Key, assetId } = await imageStudioService.uploadReference(
          activeGalleryId,
          prepared
        );
        setReferenceKey(s3Key);
        setParentAssetId(assetId);
        setMaskB64(null); // a mask painted over the previous image is meaningless now
        setReferencePreviewUrl((prev) => {
          if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
          return URL.createObjectURL(prepared);
        });
        refreshAssets(); // the upload is now a first-class asset
      } catch (err) {
        console.error("Reference upload failed:", err);
        toast.error("Failed to upload reference image");
      } finally {
        setUploadingReference(false);
      }
    },
    [activeGalleryId, refreshAssets]
  );

  // ── Generate (preset or raw tool) ──
  const generate = useCallback(async () => {
    if (!activeGalleryId) {
      toast.error("Select or create a gallery first");
      return;
    }

    // Deterministic tools (no seed param) always run as a single variant —
    // N identical results would only waste inference cost.
    const effectiveVariants =
      presetMode && activePreset
        ? variants
        : currentSpec?.params.some((p) => p.name === "seed")
        ? variants
        : 1;

    let request: any;
    if (presetMode && activePreset) {
      const needsImage = activePreset.fields.some(
        (f) => f.type === "image" && f.required && f.slot !== "style"
      );
      const needsStyle = activePreset.fields.some(
        (f) => f.type === "image" && f.required && f.slot === "style"
      );
      if (needsImage && !referenceKey) {
        toast.error("Upload a photo first.");
        return;
      }
      if (needsStyle && !styleReferenceKey) {
        toast.error("Upload a style image first.");
        return;
      }
      if (presetNeedsMask && !maskB64) {
        toast.error("Paint the area to change first.");
        return;
      }
      request = {
        preset: activePreset.key,
        gallery_id: activeGalleryId,
        params,
        reference_key: referenceKey,
        style_reference_key: needsStyle ? styleReferenceKey : null,
        parent_asset_id: parentAssetId,
        mask_b64: presetNeedsMask ? maskB64 : null,
        variants: effectiveVariants,
      };
    } else {
      if (!currentSpec) return;
      if (currentSpec.needs_image && !referenceKey) {
        toast.error("This tool needs a reference image. Upload one first.");
        return;
      }
      if (currentSpec.needs_style_image && !styleReferenceKey) {
        toast.error("Upload a style image first.");
        return;
      }
      const sendsMask = !!currentSpec.needs_mask;
      if (sendsMask && !maskB64) {
        toast.error("Paint a mask over the area to change first.");
        return;
      }
      request = {
        tool: currentSpec.key,
        gallery_id: activeGalleryId,
        params,
        reference_key: referenceKey,
        parent_asset_id: parentAssetId,
        mask_b64: sendsMask ? maskB64 : null,
        style_reference_key: currentSpec.needs_style_image ? styleReferenceKey : null,
        variants: effectiveVariants,
      };
    }

    try {
      setGenerating(true);
      const response = await imageStudioService.generate(request);
      // The user may have switched galleries while this ran. The image is
      // safely stored in the gallery it was requested from — don't leak its
      // result or asset list into the gallery now on screen.
      if (activeGalleryIdRef.current !== request.gallery_id) {
        toast.success("Image ready — saved in the gallery it was started from");
        return;
      }
      const batch: StudioResult[] = response.map((r) => ({
        assetId: r.asset_id,
        url: r.presigned_url,
        s3Key: r.s3_key,
        tool: r.tool,
        seed: r.seed,
        outputFormat: r.output_format,
      }));
      setResults(batch);
      setResult(batch[0] ?? null);
      // Pin the returned seed only for single-variant raw-tool runs (the dice
      // re-randomizes). Presets have no seed control, and a multi-variant batch
      // has one seed per image, so pinning would be wrong in both cases.
      if (batch.length === 1 && batch[0].seed && !(presetMode && activePreset)) {
        setParam("seed", batch[0].seed);
      }
      await refreshAssets();
      toast.success(batch.length > 1 ? `${batch.length} variants ready` : "Image ready");
    } catch (err: any) {
      const detail = err?.response?.data?.detail || "Generation failed. Please try again.";
      console.error("Generation failed:", err);
      toast.error(detail);
    } finally {
      setGenerating(false);
    }
  }, [presetMode, activePreset, presetNeedsMask, currentSpec, activeGalleryId, referenceKey, maskB64, styleReferenceKey, params, parentAssetId, variants, refreshAssets, setParam]);

  const selectResult = useCallback(
    (assetId: string) => {
      setResult((prev) => results.find((r) => r.assetId === assetId) ?? prev);
    },
    [results]
  );

  const clearResult = useCallback(() => {
    setResult(null);
    setResults([]);
  }, []);

  const applySettings = useCallback(
    (asset: StudioAsset) => {
      const stored = asset.params || {};
      if (asset.mode === "preset") {
        const preset = presets.find((p) => p.key === asset.tool);
        if (!preset) {
          toast.error("The preset that made this image is no longer available");
          return;
        }
        setPresetMode(true);
        setActivePresetKey(preset.key);
        const base = presetDefaults(preset);
        preset.fields.forEach((f) => {
          if (stored[f.name] !== undefined) base[f.name] = stored[f.name];
        });
        setParams(base);
      } else {
        const spec = tools.find((t) => t.key === asset.tool);
        if (!spec) {
          toast.error("The tool that made this image is no longer available");
          return;
        }
        setPresetMode(false);
        setActivePresetKey(null);
        setToolKey(spec.key);
        const base = defaultsFor(spec);
        spec.params.forEach((p) => {
          if (stored[p.name] !== undefined) base[p.name] = stored[p.name];
        });
        // The actual seed the model used beats whatever was requested —
        // regenerating with it reproduces this exact image.
        if (asset.seed != null && spec.params.some((p) => p.name === "seed")) {
          base.seed = asset.seed;
        }
        setParams(base);
      }
      setMaskB64(null); // masks aren't stored; brush tools need a fresh one
      // Restore the source image too, when it still exists in this gallery.
      if (asset.parent_asset_id) {
        const parent = assets.find((a) => a.asset_id === asset.parent_asset_id);
        if (parent) {
          setReferenceKey(parent.s3_key);
          setParentAssetId(parent.asset_id);
          setReferencePreviewUrl((prev) => {
            if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
            return parent.presigned_url;
          });
        }
      }
      toast.info("Settings loaded — tweak anything and Generate");
    },
    [presets, tools, assets]
  );

  const loadAsInput = useCallback(
    (asset: { s3_key: string; presigned_url: string; asset_id: string }) => {
      setReferenceKey(asset.s3_key);
      setParentAssetId(asset.asset_id);
      setMaskB64(null); // any painted mask belonged to the previous image
      setReferencePreviewUrl((prev) => {
        if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
        return asset.presigned_url;
      });

      // Stay where the user is whenever the current surface can take an input
      // image — the image is then equally usable in any preset or tool tab.
      if (presetMode) {
        toast.info(
          activePreset
            ? "Image loaded — adjust the options and Generate"
            : "Image loaded — pick a preset to use it"
        );
        return;
      }
      const spec = tools.find((t) => t.key === toolKey);
      const acceptsImage =
        !!spec && (spec.needs_image || spec.optional_image || spec.key === "sd3.5");
      if (acceptsImage) {
        // SD3.5 only consumes the image in image-to-image mode — switch it over.
        if (spec!.key === "sd3.5") setParam("mode", "image-to-image");
        toast.info("Image loaded as input");
      } else {
        const firstEdit = tools.find((t) => t.mode === "edit");
        if (firstEdit) selectTool(firstEdit.key);
        toast.info("Image loaded — pick an edit tool and adjust");
      }
    },
    [presetMode, activePreset, tools, toolKey, selectTool, setParam]
  );

  const deleteItem = useCallback(
    async (assetId: string) => {
      if (!activeGalleryId) return;
      const deleted = assets.find((a) => a.asset_id === assetId);
      try {
        await imageStudioService.deleteAsset(activeGalleryId, assetId);
        setAssets((prev) => prev.filter((a) => a.asset_id !== assetId));
        setGalleries((prev) =>
          prev.map((g) =>
            g.gallery_id === activeGalleryId
              ? { ...g, asset_count: Math.max((g.asset_count || 0) - 1, 0) }
              : g
          )
        );
        // Prune it from the last variant batch; move the selection to the
        // next surviving variant so the canvas never shows a deleted image.
        const remaining = results.filter((r) => r.assetId !== assetId);
        if (remaining.length !== results.length) setResults(remaining);
        if (result?.assetId === assetId) setResult(remaining[0] ?? null);
        // If the deleted asset was loaded as an input, its S3 object is gone —
        // generating from it would fail, so detach it.
        if (deleted?.s3_key && deleted.s3_key === referenceKey) clearReference();
        if (deleted?.s3_key && deleted.s3_key === styleReferenceKey) clearStyleReference();
      } catch (err) {
        console.error("Delete failed:", err);
        toast.error("Failed to delete image");
      }
    },
    [activeGalleryId, assets, result, results, referenceKey, styleReferenceKey, clearReference, clearStyleReference]
  );

  const value: ImageStudioContextType = {
    presets,
    presetsLoading,
    presetMode,
    activePreset,
    presetNeedsMask,
    openPresets,
    exitPresets,
    selectPreset,
    closePreset,
    galleries,
    activeGalleryId,
    activeGallery,
    galleriesLoading,
    createGallery,
    renameGallery,
    deleteGallery,
    switchGallery,
    tools,
    modes,
    mode,
    setMode,
    toolKey,
    selectTool,
    currentSpec,
    toolsForMode,
    params,
    setParam,
    randomizeSeed,
    catalogLoading,
    referenceKey,
    referencePreviewUrl,
    uploadingReference,
    uploadReference,
    clearReference,
    maskB64,
    setMaskB64,
    styleReferenceKey,
    styleReferencePreviewUrl,
    uploadingStyleReference,
    uploadStyleReference,
    clearStyleReference,
    result,
    results,
    selectResult,
    variants,
    setVariants,
    generating,
    generate,
    clearResult,
    loadAsInput,
    applySettings,
    assets,
    loadingAssets,
    refreshAssets,
    deleteItem,
  };

  return (
    <ImageStudioContext.Provider value={value}>{children}</ImageStudioContext.Provider>
  );
};

export const useImageStudio = () => {
  const context = useContext(ImageStudioContext);
  if (!context) {
    throw new Error("useImageStudio must be used within an ImageStudioProvider");
  }
  return context;
};
